import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Folder, Wrench, Layers } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatorModeSelector = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const options = [
    {
      key: 'PORTFOLIO_CREATOR',
      icon: Folder,
      title: 'Create Portfolios',
      desc: 'Launch NFT portfolios with utilities, manage collections, and attract buyers.',
      color: theme.colors.neon.blue
    },
    {
      key: 'UTILITY_PROVIDER',
      icon: Wrench,
      title: 'Publish Utilities',
      desc: 'List utility offerings on the marketplace for other creators to attach to their portfolios.',
      color: theme.colors.accent.teal
    },
    {
      key: 'BOTH',
      icon: Layers,
      title: 'Both',
      desc: 'Create portfolios and publish utilities. Full access to all creator tools.',
      color: theme.colors.primary
    }
  ];

  const handleConfirm = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const modes = selected === 'BOTH'
        ? ['PORTFOLIO_CREATOR', 'UTILITY_PROVIDER']
        : [selected];
      const defaultMode = selected === 'BOTH' ? 'PORTFOLIO_CREATOR' : selected;

      await axios.post(`${API}/creator/set-modes`, {
        creator_modes: modes.includes('PORTFOLIO_CREATOR') && modes.includes('UTILITY_PROVIDER') ? ['BOTH'] : modes,
        default_mode: defaultMode
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (onComplete) onComplete();
      navigate('/creator/dashboard');
    } catch (err) {
      console.error('Error setting mode:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bg.page,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '720px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 data-testid="mode-selector-title" style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: 700,
            color: theme.colors.text.primary,
            marginBottom: '12px'
          }}>
            What do you want to do?
          </h1>
          <p style={{ fontSize: '15px', color: theme.colors.text.secondary }}>
            Choose how you'd like to use the platform. You can change this later.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
          {options.map((opt) => {
            const isSelected = selected === opt.key;
            return (
              <div
                key={opt.key}
                data-testid={`mode-option-${opt.key.toLowerCase()}`}
                onClick={() => setSelected(opt.key)}
                style={{
                  padding: '28px 20px',
                  borderRadius: theme.radius.card,
                  border: `2px solid ${isSelected ? opt.color : theme.colors.borders}`,
                  background: isSelected ? `${opt.color}08` : theme.colors.bg.card,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'center',
                  boxShadow: isSelected ? theme.colors.shadows.md : theme.colors.shadows.sm
                }}
              >
                <div style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '14px',
                  background: `${opt.color}12`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <opt.icon size={26} style={{ color: opt.color }} />
                </div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.colors.text.primary,
                  marginBottom: '8px'
                }}>
                  {opt.title}
                </h3>
                <p style={{ fontSize: '13px', color: theme.colors.text.secondary, lineHeight: '1.5' }}>
                  {opt.desc}
                </p>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            data-testid="mode-confirm-btn"
            onClick={handleConfirm}
            disabled={!selected || loading}
            style={{
              padding: '14px 48px',
              background: selected ? theme.colors.primary : theme.colors.bg.tertiary,
              color: selected ? '#FFFFFF' : theme.colors.text.muted,
              border: 'none',
              borderRadius: theme.radius.cta,
              fontSize: '15px',
              fontWeight: 600,
              cursor: selected ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatorModeSelector;
